import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { hotjar } from 'react-hotjar';
import { useHistory, useLocation, withRouter } from 'react-router-dom';
import { useGrowthBook } from '@growthbook/growthbook-react';
import 'react-toastify/dist/ReactToastify.css';
import platform from 'platform';

import Navbar from './containers/Navbar';
import Footer from './components/Footer';
import Routes from './routes/Routes';

import styles from './style/containers/App.module.scss';
import { getIntroClass } from './actions/sessions';
import { setSentryUser } from './setup/sentry';
import TriggerGuideHandler from './components/study-area/TriggerGuideHandler';
import LicenseExpiringModal from './components/license-expiring-modal';
import { hideLicenseExpiringModal, showLicenseExpiringModal } from './actions/license-expiring-modal';
import { refreshUser } from './actions/user';

const URLS_TO_HIDE_FOOTER = ['onboarding', 'assignment', 'impersonate'];
const URLS_TO_HIDE_NAVBAR = ['onboarding', 'welcome'];

const App = ({
  getIntroClassAction,
  orderInfo,
  token,
  user,
  selectedCourse,
  timezone,
  licenseExpiringModal,
  hideLicenseExpiringModalAction,
  showLicenseExpiringModalAction,
  refreshUserAction
}) => {
  const location = useLocation();
  const history = useHistory();
  const growthBook = useGrowthBook();

  useEffect(() => {
    getIntroClassData({ user, orderInfo });
  }, []);

  useEffect(() => {
    console.log('process.env.REACT_APP_HOTJAR_ID', process.env.REACT_APP_HOTJAR_ID);
    if (process.env.REACT_APP_HOTJAR_ID) {
      console.log('initialize hotjar');
      hotjar.initialize({ id: process.env.REACT_APP_HOTJAR_ID, sv: 6 });
    }
  }, []);

  useEffect(() => {
    if (user?.id) setSentryUser(user);
  }, [user?.id]);

  useEffect(() => {
    console.log('Updating GrowthBook attributes based on user:', user);
    if (user) {
      growthBook.setAttributes({
        userId: user.id,
        email: user.email,
        isLoggedIn: !!user
      });
      console.log('GrowthBook attributes set:', {
        userId: user.id,
        email: user.email,
        isLoggedIn: !!user
      });
    } else {
      growthBook.setAttributes({
        userId: null,
        email: null,
        isLoggedIn: false
      });
    }
  }, [user, growthBook]);

  // UserGuiding identification function
  const identifyUserGuidingUser = useCallback(() => {
    console.log('identifyUserGuidingUser called');

    if (!window?.userGuiding?.identify) {
      console.log('UserGuiding identify function not available');
      return false;
    }

    if (!user?.id) {
      console.log('No user ID available for UserGuiding');
      return false;
    }

    console.log('Identifying UG user');

    try {
      window.userGuiding.identify(user.id, {
        user_type: user.user_type,
        first_name: user.first_name,
        last_name: user.last_name,
        licence_type: user.licence_type,
        email: user.email,
        organization: user?.organization?.[0],
        language: selectedCourse?.language,
        language_level: selectedCourse?.name,
        next_lesson_number: selectedCourse?.next_lesson_happening,
        time_zone: timezone,
        browser: platform.name,
        os: platform.os.toString()
      });
      console.log('UserGuiding identification successful');
      return true;
    } catch (error) {
      console.error('Error identifying user with UserGuiding:', error);
      return false;
    }
  }, [user, selectedCourse, timezone]);

  // UserGuiding identification with load detection
  useEffect(() => {
    if (!user?.id) return;

    // Try immediate identification
    if (identifyUserGuidingUser()) {
      return; // Success, no need for polling
    }

    // If immediate identification fails, set up polling
    const pollForUserGuiding = () => {
      let attempts = 0;
      const maxAttempts = 20;
      const pollInterval = 250;

      const poll = () => {
        attempts++;
        console.log(`Polling for UserGuiding... attempt ${attempts}/${maxAttempts}`);

        if (identifyUserGuidingUser()) {
          console.log('UserGuiding identification successful via polling');
          return; // Success
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.log('UserGuiding failed to load after maximum polling attempts');
        }
      };

      poll();
    };

    // Start polling
    pollForUserGuiding();

    // Also listen for UserGuiding load event if it exists
    const handleUserGuidingLoad = () => {
      console.log('UserGuiding load event detected');
      identifyUserGuidingUser();
    };

    // Check if UserGuiding provides a load event
    if (window.userGuidingLayer) {
      window.userGuidingLayer.push(['onReady', handleUserGuidingLoad]);
    }

    return () => {
      // Cleanup if needed
    };
  }, [user?.id, identifyUserGuidingUser]);

  useEffect(() => {
    if (hotjar && hotjar.initialized() && user) {
      hotjar.identify(user.id, {
        email: user.email,
        company: user.organization?.name,
        preferred_language: user.preferred_language,
        user_type: user.user_type
      });
    } else if (hotjar && hotjar.initialized()) {
      hotjar.identify(null, {});
    }
  }, [user, hotjar]);

  // Re-fetch the current user from the backend whenever the tab becomes
  // visible again. Without this, server-side changes to the user object
  // (license extensions, org-flag flips, plan changes) never reach an
  // already-open browser session because the user payload is persisted
  // to IndexedDB on login and reused indefinitely.
  useEffect(() => {
    if (!token) return undefined;

    let lastRefreshAt = 0;
    const THROTTLE_MS = 30 * 1000;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastRefreshAt < THROTTLE_MS) return;
      lastRefreshAt = now;
      refreshUserAction(token);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token, refreshUserAction]);

  // Check for expiring license when user is already logged in
  useEffect(() => {
    if (!user?.id || user?.user_type !== 'student') return;
    if (!user?.license_expiring_soon || !user?.days_until_license_expires) return;

    // Check if we've shown the modal in the last 24 hours
    const lastShownKey = 'licenseExpiringModalLastShown';
    const lastShown = localStorage.getItem(lastShownKey);
    const now = new Date().getTime();
    const oneDayInMs = 24 * 60 * 60 * 1000;

    if (lastShown && now - parseInt(lastShown, 10) < oneDayInMs) {
      // Already shown in the last 24 hours, don't show again
      return;
    }

    // Show the modal and update the timestamp
    localStorage.setItem(lastShownKey, now.toString());
    showLicenseExpiringModalAction(user.days_until_license_expires);
  }, [user?.id, user?.user_type, user?.license_expiring_soon, user?.days_until_license_expires, showLicenseExpiringModalAction]);

  const getIntroClassData = ({ user, orderInfo }) => {
    if (!user?.token || !orderInfo?.active_orders) return;
    getIntroClassAction({ token: user?.token, languageId: orderInfo?.active_orders[0]?.order_appointment_types[0]?.language_id });
  };

  const renderHeader = () => {
    if (!token) return <div className={styles.emptyNavbar}></div>;
    if (URLS_TO_HIDE_NAVBAR.some((url) => location.pathname.includes(url))) return <div className={styles.emptyNavbar}></div>;
    return <Navbar history={history} />;
  };

  const renderFooter = () => {
    if (!token) return;
    if (URLS_TO_HIDE_FOOTER.some((url) => location.pathname.includes(url))) return;
    return <Footer user={user} />;
  };

  return (
    <div className={styles.app}>
      <TriggerGuideHandler />
      {renderHeader()}
      <main className={styles.main}>
        <Routes history={history} userType={user?.user_type} />
      </main>
      {renderFooter()}
      <LicenseExpiringModal
        isOpen={licenseExpiringModal?.showModal || false}
        onClose={hideLicenseExpiringModalAction}
        daysRemaining={licenseExpiringModal?.daysRemaining || 0}
      />
    </div>
  );
};

App.defaultProps = {
  token: '',
  user: {}
};

App.propTypes = {
  token: PropTypes.string,
  user: PropTypes.object,
  productTypes: PropTypes.array,
  orderInfo: PropTypes.object,
  getIntroClassAction: PropTypes.func.isRequired,
  selectedCourse: PropTypes.object,
  timezone: PropTypes.string,
  licenseExpiringModal: PropTypes.object,
  hideLicenseExpiringModalAction: PropTypes.func.isRequired,
  showLicenseExpiringModalAction: PropTypes.func.isRequired,
  refreshUserAction: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  token: state.user?.token,
  user: state.user,
  orderInfo: state.orderInfo,
  courses: state.courses,
  selectedCourse: state.courses?.selectedCourseDetails?.courseDetails,
  timezone: state.time?.timezone,
  licenseExpiringModal: state.licenseExpiringModal
});

export default withRouter(
  connect(mapStateToProps, {
    getIntroClassAction: getIntroClass,
    hideLicenseExpiringModalAction: hideLicenseExpiringModal,
    showLicenseExpiringModalAction: showLicenseExpiringModal,
    refreshUserAction: refreshUser
  })(App)
);
